import { defineStore } from "pinia";
import { computed, ref, toValue, type ComputedRef } from "vue";
import { useChannelStore } from "./channel.store";
import { useLazyServices } from "@/composables/use-lazy-services";
import type { setupWalletCapability } from "@/services/wallet/wallet.service";
import type { setupMembershipCapability } from "@/services/membership/membership.service";
import type { setupPurchaseService } from "@/services/purchase/purchase.service";

/**
 * Interface that capability services must implement.
 * Each service must expose an `isReady` computed that indicates
 * when the service has completed its internal initialization.
 *
 * Note: When accessed through the capabilities store, the ComputedRef
 * is auto-unwrapped by Vue's reactivity system, so `isReady` becomes `boolean`.
 */
export interface CapabilityService {
  isReady: ComputedRef<boolean>;
  dispose: () => void;
}

// Types for service instances
type WalletCapabilityInstance = ReturnType<typeof setupWalletCapability>;
type MembershipCapabilityInstance = ReturnType<
  typeof setupMembershipCapability
>;
type PurchaseServiceInstance = ReturnType<typeof setupPurchaseService>;

interface CapabilityServices {
  wallet: WalletCapabilityInstance;
  membership: MembershipCapabilityInstance;
  purchase: PurchaseServiceInstance;
}

export const useCapabilitiesStore = defineStore("capabilities", () => {
  const channelStore = useChannelStore();

  const walletEnabled = computed(
    () => channelStore.channelCapabilities?.wallet_enabled ?? false,
  );
  const membershipsEnabled = computed(
    () => channelStore.channelCapabilities?.memberships_enabled ?? false,
  );

  // Capability config based on channel capabilities
  const capabilityConfig = computed(() => ({
    wallet: walletEnabled.value,
    membership: membershipsEnabled.value,
    purchase: membershipsEnabled.value,
  }));

  // Lazy load capability services based on config
  const { services, isReady: servicesLoaded } = useLazyServices<
    "wallet" | "membership" | "purchase",
    typeof capabilityConfig.value,
    CapabilityServices,
    undefined
  >({
    config: capabilityConfig,
    definitions: {
      wallet: {
        load: () =>
          import("@/services/wallet/wallet.service").then((m) => ({
            create: m.setupWalletCapability,
          })),
      },
      membership: {
        load: () =>
          import("@/services/membership/membership.service").then((m) => ({
            create: m.setupMembershipCapability,
          })),
      },
      purchase: {
        load: () =>
          import("@/services/purchase/purchase.service").then((m) => ({
            create: m.setupPurchaseService,
          })),
      },
    },
    deps: undefined,
  });

  // Computed accessors for individual services
  const walletService = computed(() => services.value.wallet ?? null);
  const membershipService = computed(() => services.value.membership ?? null);
  const purchaseService = computed(() => services.value.purchase ?? null);

  // Combined isReady: services are loaded AND each service is internally ready
  // Note: services accessed through refs are auto-unwrapped, so isReady is boolean not ComputedRef
  const isReady = computed(() => {
    // First check if lazy loading is complete
    if (!servicesLoaded.value) return false;

    return Object.values(services.value).every((service) =>
      toValue(service.isReady),
    );
  });

  const isUpgrading = ref(false);

  return {
    // Services
    walletService,
    membershipService,
    purchaseService,

    // State
    isReady,
    isUpgrading,
  };
});
